<!-- ============================================================ -->
<!-- VIEW 1: Returns Listing (default)                          -->
<!-- ============================================================ -->
@if (currentView === 'returns') {

<div class="cashering-container">

  <!-- Breadcrumb -->
  <nav class="breadcrumb" aria-label="Breadcrumb">
    <span class="breadcrumb-text">Cashiering /</span>
  </nav>

  <!-- Page heading -->
  <h1 class="page-title">Cashiering</h1>
  <p class="page-subtitle">Search a customer below to take a payment. To manage cash drawers, use Cash Drawers in the side menu.</p>

  <!-- ============================================================ -->
  <!-- Cash drawer panel                                          -->
  <!-- ============================================================ -->
  <!-- Open drawer banner -->
  <div class="drawer-banner open" *ngIf="currentDrawer; else noDrawer">
    <span class="db-icon"><mat-icon>account_balance_wallet</mat-icon></span>
    <div class="db-body">
      <div class="db-head">
        <span class="db-title">Your cash drawer</span>
        <span class="db-badge open"><span class="dot"></span>Open</span>
      </div>
      <div class="db-meta">
        <span>Drawer #{{ currentDrawer.id }}</span>
        <span>{{ currentDrawer.paymentOfficeName }}</span>
        <span>Opened {{ currentDrawer.openedAt | date:'MM/dd/yyyy h:mm a' }}</span>
        <span>Opening {{ currentDrawer.openingCash | currency }}</span>
      </div>
    </div>
    <div class="db-actions">
      <button *ngIf="currentDrawer.editable" mat-stroked-button color="primary" (click)="editDrawer()"
              title="Correct the department or opening float — available until the first payment">
        <mat-icon>edit</mat-icon> Edit</button>
      <button mat-stroked-button color="primary" [routerLink]="['/app/cashering/drawer', currentDrawer.id]">
        <mat-icon>visibility</mat-icon> View</button>
      <button mat-flat-button color="primary" (click)="closeDrawer(currentDrawer.id)">
        <mat-icon>lock</mat-icon> Close &amp; settle</button>
    </div>
  </div>
  <ng-template #noDrawer>
    <div class="drawer-banner closed">
      <span class="db-icon muted"><mat-icon>lock</mat-icon></span>
      <div class="db-body">
        <div class="db-title">No open cash drawer</div>
        <div class="db-sub">Open a drawer to begin taking cash payments this shift.</div>
      </div>
      <div class="db-actions">
        <button mat-flat-button color="primary" (click)="openDrawer()">
          <mat-icon>point_of_sale</mat-icon> Open drawer</button>
      </div>
    </div>
  </ng-template>

  <!-- Incoming transfers -->
  <div class="drawer-panel transfers" *ngIf="pendingTransfers.length">
    <div class="panel-head"><mat-icon>swap_horiz</mat-icon> Incoming cash transfers
      <span class="count">{{ pendingTransfers.length }}</span></div>
    <div *ngFor="let t of pendingTransfers" class="transfer-row">
      <span class="t-amt">{{ t.amount | currency }}</span>
      <span class="t-from">from <b>{{ t.fromCashierName }}</b><span *ngIf="t.note" class="t-note">“{{ t.note }}”</span></span>
      <span class="t-actions">
        <button mat-flat-button color="primary" (click)="acceptTransfer(t)">Accept</button>
        <button mat-stroked-button (click)="rejectTransfer(t)">Reject</button>
      </span>
    </div>
  </div>

  <!-- Previous drawers -->
  <div class="drawer-panel" *ngIf="pastDrawers.length">
    <div class="panel-head"><mat-icon>history</mat-icon> Previous drawers</div>
    <table class="past">
      <thead><tr><th>Date</th><th class="num">Opening</th><th class="num">Declared</th><th class="num">Over / Short</th><th class="act"></th></tr></thead>
      <tbody>
        <tr *ngFor="let d of pastDrawers">
          <td>{{ d.businessDate | date }}</td>
          <td class="num">{{ d.openingCash | currency }}</td>
          <td class="num">{{ d.declaredCash | currency }}</td>
          <td class="num"><span class="os" [ngClass]="d.overShort == null || d.overShort === 0 ? 'bal' : d.overShort > 0 ? 'over' : 'short'">{{ d.overShort | currency }}</span></td>
          <td class="act"><a mat-button color="primary" [routerLink]="['/app/cashering/drawer', d.id]">Open</a></td>
        </tr>
      </tbody>
    </table>
  </div>

  <!-- Search row -->
  <div class="search-row" role="search">
    <mat-form-field appearance="outline" class="search-field">
      <mat-label>Search</mat-label>
      <input
        matInput
        placeholder="Search by name, email, phone, address or ID"
        [(ngModel)]="searchQuery"
        (keyup.enter)="searchReturns()"
        aria-label="Search by name, email, phone, address or ID"
      >
      <mat-icon matSuffix aria-hidden="true">search</mat-icon>
    </mat-form-field>

    <mat-form-field appearance="outline" class="filter-field">
      <mat-label>Year</mat-label>
      <mat-select [(ngModel)]="filterYear" (selectionChange)="applyFilters()" aria-label="Filter by year">
        <mat-option [value]="''">All</mat-option>
        @for (year of availableYears; track year) {
          <mat-option [value]="year">{{ year }}</mat-option>
        }
      </mat-select>
    </mat-form-field>

    <mat-form-field appearance="outline" class="filter-field">
      <mat-label>Month</mat-label>
      <mat-select [(ngModel)]="filterMonth" (selectionChange)="applyFilters()" aria-label="Filter by month">
        <mat-option value="">All</mat-option>
        @for (m of months; track m) {
          <mat-option [value]="m">{{ m }}</mat-option>
        }
      </mat-select>
    </mat-form-field>

    <mat-form-field appearance="outline" class="filter-field">
      <mat-label>Return status</mat-label>
      <!-- Only the statuses this list can actually contain: it loads outstanding returns
           (PENDING, UNPAID, PARTIALLY_PAID, OVERDUE), so a Paid option could never match. -->
      <mat-select [(ngModel)]="filterStatus" (selectionChange)="applyFilters()" aria-label="Filter by return status">
        <mat-option value="">All</mat-option>
        <mat-option value="PENDING">Pending</mat-option>
        <mat-option value="UNPAID">Unpaid</mat-option>
        <mat-option value="OVERDUE">Overdue</mat-option>
        <mat-option value="PARTIALLY_PAID">Partial</mat-option>
      </mat-select>
    </mat-form-field>

    <button
      mat-stroked-button
      color="primary"
      class="search-btn"
      (click)="searchReturns()"
      [disabled]="isLoading"
      aria-label="Search for customer"
    >
      @if (isLoading) {
        <mat-spinner diameter="16" class="search-spinner"></mat-spinner>
      } @else {
        <mat-icon aria-hidden="true">search</mat-icon>
      }
      SEARCH
    </button>
  </div>

  <!-- Active filter chip: the selected customer (chosen from the search results modal) -->
  @if (hasCustomer && customerName) {
    <div class="active-filters" aria-label="Active filters">
      <mat-icon aria-hidden="true">filter_list</mat-icon>
      <span class="active-filters-label">Active filters</span>
      <mat-chip-set>
        <mat-chip class="customer-chip" [attr.aria-label]="'Remove filter: ' + customerName + ' | ' + customerId">
          {{ customerName }} | {{ customerId }}
          <button matChipRemove (click)="clearCustomer()" aria-label="Remove customer filter">
            <mat-icon>cancel</mat-icon>
          </button>
        </mat-chip>
      </mat-chip-set>
    </div>
  }

  <!-- Loading overlay -->
  @if (isLoading) {
    <div class="loading-overlay" aria-live="polite" aria-label="Loading, please wait">
      <mat-spinner diameter="40"></mat-spinner>
      <p class="loading-text">{{ isSearching ? 'Searching…' : 'Loading returns…' }}</p>
    </div>
  }

  <!-- Returns table (shown after a customer is selected) -->
  @if (!isLoading && hasCustomer && filteredReturns.length > 0) {
    <div class="returns-table-wrapper">
      <table
        mat-table
        [dataSource]="pagedReturns"
        class="returns-table mat-elevation-z1"
        aria-label="Outstanding returns"
      >

        <!-- Return Year -->
        <ng-container matColumnDef="year">
          <th mat-header-cell *matHeaderCellDef>Return Year</th>
          <td mat-cell *matCellDef="let ret">{{ getPeriodYear(ret.period) }}</td>
        </ng-container>

        <!-- Return Month -->
        <ng-container matColumnDef="month">
          <th mat-header-cell *matHeaderCellDef>Return Month</th>
          <td mat-cell *matCellDef="let ret">{{ getPeriodMonth(ret.period) }}</td>
        </ng-container>

        <!-- Return Type -->
        <ng-container matColumnDef="type">
          <th mat-header-cell *matHeaderCellDef>Return Type</th>
          <td mat-cell *matCellDef="let ret">
            {{ ret.type }}
            <span class="shared-badge" *ngIf="ret.shared">Shared from {{ ret.owningAccountName }}</span>
            <div class="reported-by" *ngIf="ret.reportedByRole">Reported by {{ ret.reportedByRole === 'MANAGEMENT_COMPANY' ? 'Management Co' : 'Owner' }}</div>
          </td>
        </ng-container>

        <!-- Frequency -->
        <ng-container matColumnDef="frequency">
          <th mat-header-cell *matHeaderCellDef>Frequency</th>
          <td mat-cell *matCellDef="let ret">{{ ret.frequency }}</td>
        </ng-container>

        <!-- Total Amount Due -->
        <ng-container matColumnDef="totalAmountDue">
          <th mat-header-cell *matHeaderCellDef>Total Amount Due</th>
          <td mat-cell *matCellDef="let ret" class="amount-cell">{{ ret.totalAmountDue | currency }}</td>
        </ng-container>

        <!-- Status -->
        <ng-container matColumnDef="status">
          <th mat-header-cell *matHeaderCellDef>Status</th>
          <td mat-cell *matCellDef="let ret">
            <span
              class="status-badge"
              [ngClass]="getStatusClass(ret.status)"
              [attr.aria-label]="'Status: ' + getStatusLabel(ret.status)"
            >
              <mat-icon class="badge-icon" aria-hidden="true">{{ getStatusIcon(ret.status) }}</mat-icon>
              {{ getStatusLabel(ret.status) }}
            </span>
          </td>
        </ng-container>

        <!-- Actions -->
        <ng-container matColumnDef="actions">
          <th mat-header-cell *matHeaderCellDef>Actions</th>
          <td mat-cell *matCellDef="let ret">
            <div class="action-buttons">
              <button
                mat-icon-button
                (click)="openReturnDetail(ret)"
                [attr.aria-label]="'View return ' + ret.period"
                title="View"
              >
                <mat-icon>visibility</mat-icon>
              </button>
              <button
                mat-icon-button
                class="add-to-basket-btn"
                [class.already-in-basket]="isInBasket(ret.id)"
                (click)="addToBasket(ret)"
                [disabled]="isInBasket(ret.id)"
                [attr.aria-label]="isInBasket(ret.id) ? 'Already in basket: ' + ret.period : 'Add to basket: ' + ret.period"
                title="{{ isInBasket(ret.id) ? 'Already in basket' : 'Add to basket' }}"
              >
                <mat-icon>{{ isInBasket(ret.id) ? 'check_circle' : 'add_circle' }}</mat-icon>
              </button>
              @if (isInBasket(ret.id)) {
                <button mat-icon-button class="remove-from-basket-btn" (click)="confirmRemoveReturn(ret.id)"
                        [attr.aria-label]="'Remove from basket: ' + ret.period" title="Remove from basket">
                  <mat-icon>remove_circle_outline</mat-icon>
                </button>
              }
            </div>
          </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="returnsDisplayedColumns"></tr>
        <tr mat-row *matRowDef="let row; columns: returnsDisplayedColumns;"></tr>
      </table>

      <mat-paginator
        [length]="filteredReturns.length"
        [pageSize]="pageSize"
        [pageSizeOptions]="[5, 10, 25]"
        (page)="onPageChange($event)"
        aria-label="Select page of returns"
      ></mat-paginator>
    </div>
  }

  <!-- Filters excluded everything: distinct from the customer having no returns at all,
       so the cashier knows to widen the filters rather than believing nothing is owed. -->
  @if (!isLoading && hasCustomer && allReturns.length > 0 && filteredReturns.length === 0) {
    <div class="no-returns-state" role="status">
      <mat-icon class="no-returns-icon" aria-hidden="true">filter_alt_off</mat-icon>
      <h2>No returns match these filters</h2>
      <p>{{ customerName }} has {{ allReturns.length }} outstanding
        {{ allReturns.length === 1 ? 'return' : 'returns' }} — widen the year, month or status.</p>
    </div>
  }

  <!-- Empty returns state: customer loaded but no returns -->
  @if (!isLoading && hasCustomer && allReturns.length === 0) {
    <div class="no-returns-state" role="status">
      <mat-icon class="no-returns-icon" aria-hidden="true">receipt_long</mat-icon>
      <h2>No outstanding returns</h2>
      <p>There are no unpaid or pending returns for {{ customerName }}.</p>
    </div>
  }

  <!-- Load error state -->
  @if (!isLoading && hasLoadError) {
    <div class="load-error-state" role="alert">
      <mat-icon aria-hidden="true">error_outline</mat-icon>
      <p>Failed to load returns. Please try again.</p>
      <button mat-stroked-button color="primary" (click)="loadOutstandingReturns()">Retry</button>
    </div>
  }

  <!-- No customer selected yet -->
  @if (!isLoading && !hasCustomer && !hasLoadError) {
    <div class="no-customer-state" role="status">
      <mat-icon class="no-customer-icon" aria-hidden="true">search</mat-icon>
      <h2>No customer selected yet</h2>
      <p>Start by entering a customer's name or ID to search for their records</p>
    </div>
  }

  <!-- Floating basket button -->
  <button
    mat-fab
    extended
    class="basket-fab"
    [class.has-items]="basketItems.length > 0"
    (click)="currentView = 'basket'"
    [attr.aria-label]="'Open basket with ' + basketItems.length + ' item' + (basketItems.length === 1 ? '' : 's')"
  >
    <mat-icon aria-hidden="true">shopping_cart</mat-icon>
    BASKET ({{ basketItems.length }})
  </button>

</div>

}

<!-- ============================================================ -->
<!-- VIEW 2: Basket / Payment                                    -->
<!-- ============================================================ -->
@if (currentView === 'basket') {

<div class="cashering-container">

  <!-- Breadcrumb -->
  <nav class="breadcrumb" aria-label="Breadcrumb">
    <span class="breadcrumb-text">Payments / {{ customerName }}</span>
  </nav>

  <!-- Page heading (visually hidden but present for screen readers) -->
  <h1 class="sr-only">Cashering — {{ customerName }}</h1>

  <!-- Loading overlay -->
  @if (isLoading) {
    <div class="loading-overlay" aria-live="polite" aria-label="Loading returns, please wait">
      <mat-spinner diameter="40"></mat-spinner>
      <p class="loading-text">Loading returns&hellip;</p>
    </div>
  }

  <div class="content-wrapper" [class.loading-blur]="isLoading">

    <!-- ======================================= -->
    <!-- LEFT: Basket                            -->
    <!-- ======================================= -->
    <section class="basket-section" aria-labelledby="basket-heading">
      <div class="basket-header">
        <mat-icon aria-hidden="true">shopping_basket</mat-icon>
        <h2 id="basket-heading">Basket</h2>
      </div>

      <!-- Return cards -->
      @if (!isLoading && basketItems.length > 0) {
        <div class="returns-list" role="list">
          @for (ret of basketItems; track ret.id) {
            <div
              class="return-card"
              [class.expanded]="isReturnExpanded(ret.id)"
              role="listitem"
            >
              <!-- Card top row -->
              <div class="return-card-top">
                <!-- Status badge: icon + text for color-not-only rule -->
                <span
                  class="status-badge"
                  [ngClass]="getStatusClass(ret.status)"
                  [attr.aria-label]="'Status: ' + getStatusLabel(ret.status)"
                >
                  <mat-icon class="badge-icon" aria-hidden="true">{{ getStatusIcon(ret.status) }}</mat-icon>
                  {{ getStatusLabel(ret.status) }}
                </span>

                <div class="return-meta">
                  <span class="return-period">{{ ret.period }}</span>
                </div>

                <div class="return-right">
                  <span class="return-total-amount">{{ ret.totalAmountDue | currency }}</span>
                  <button
                    mat-icon-button
                    class="expand-btn"
                    (click)="toggleReturnExpanded(ret.id)"
                    (keydown)="onReturnCardKeydown($event, ret.id)"
                    [attr.aria-label]="isReturnExpanded(ret.id) ? 'Collapse details for ' + ret.period : 'Expand details for ' + ret.period"
                    [attr.aria-expanded]="isReturnExpanded(ret.id)"
                  >
                    <mat-icon>{{ isReturnExpanded(ret.id) ? 'expand_less' : 'expand_more' }}</mat-icon>
                  </button>
                  <button
                    mat-icon-button
                    class="remove-btn"
                    (click)="confirmRemoveReturn(ret.id)"
                    [attr.aria-label]="'Remove ' + ret.period + ' from basket'"
                  >
                    <mat-icon>delete</mat-icon>
                  </button>
                </div>

                <!-- Second line: type / frequency / properties, full width so it never
                     collides with the amount on a narrow basket -->
                <div class="return-icons">
                  <span class="icon-detail" title="Type">
                    <mat-icon aria-hidden="true">description</mat-icon>
                    <span class="icon-label">{{ ret.type }}</span>
                  </span>
                  <span class="icon-detail" title="Frequency">
                    <mat-icon aria-hidden="true">schedule</mat-icon>
                    <span class="icon-label">{{ ret.frequency }}</span>
                  </span>
                  <span class="icon-detail" title="Properties">
                    <mat-icon aria-hidden="true">home</mat-icon>
                    <span class="icon-label">{{ ret.properties?.length ?? 0 }}</span>
                  </span>
                </div>
              </div>

              <!-- Expandable breakdown -->
              @if (isReturnExpanded(ret.id)) {
                <div
                  class="return-breakdown"
                  role="region"
                  [attr.aria-label]="'Breakdown for ' + ret.period"
                >
                  <div class="breakdown-divider"></div>
                  <div class="breakdown-row">
                    <span class="breakdown-label">Taxable Rental Receipts</span>
                    <span class="breakdown-value">—</span>
                  </div>
                  <div class="breakdown-row">
                    <span class="breakdown-label">Total Taxes</span>
                    <span class="breakdown-value">—</span>
                  </div>
                  <div class="breakdown-row">
                    <span class="breakdown-label">Late Fee</span>
                    <span class="breakdown-value">—</span>
                  </div>
                  <div class="breakdown-row">
                    <span class="breakdown-label">Late Interest</span>
                    <span class="breakdown-value">—</span>
                  </div>
                </div>
              }
            </div>
          }
        </div>
      }

      <!-- Empty basket state -->
      @if (!isLoading && basketItems.length === 0) {
        <div class="empty-basket" role="status">
          <mat-icon aria-hidden="true">shopping_basket</mat-icon>
          <p class="empty-basket-title">Basket is empty</p>
          <p class="empty-basket-sub">Go back to the returns list and add items to the basket.</p>
        </div>
      }
    </section>

    <!-- ======================================= -->
    <!-- RIGHT: Payment                          -->
    <!-- ======================================= -->
    <section class="payment-section" aria-labelledby="payment-heading">
      <h2 id="payment-heading" class="sr-only">Payment</h2>

      <!-- Payment type radio -->
      <div class="payment-type-section">
        <h3 class="section-label" id="payment-type-label">Payment type</h3>
        <mat-radio-group
          [(ngModel)]="paymentType"
          (ngModelChange)="onPaymentTypeChange()"
          class="payment-type-group"
          aria-labelledby="payment-type-label"
        >
          <mat-radio-button [value]="PaymentType.MAIL_IN">Mail-in</mat-radio-button>
          <mat-radio-button [value]="PaymentType.WALK_IN">Walk-in</mat-radio-button>
        </mat-radio-group>
      </div>

      <!-- Add payment method buttons -->
      <div class="add-payment-section">
        <h3 class="section-label" id="add-method-label">Add payment method</h3>
        <div class="add-method-buttons" role="group" aria-labelledby="add-method-label">
          <button
            mat-stroked-button
            color="primary"
            (click)="addPaymentMethod(PaymentMethodType.DEBIT_CARD)"
            [disabled]="!isPaymentMethodAllowed(PaymentMethodType.DEBIT_CARD)"
            class="add-method-btn"
            aria-label="Add debit card payment method"
          >
            <mat-icon aria-hidden="true">add</mat-icon>
            DEBIT CARD
          </button>
          <button
            mat-stroked-button
            color="primary"
            (click)="addPaymentMethod(PaymentMethodType.CHECK)"
            class="add-method-btn"
            aria-label="Add check payment method"
          >
            <mat-icon aria-hidden="true">add</mat-icon>
            CHECK
          </button>
          <button
            mat-stroked-button
            color="primary"
            (click)="addPaymentMethod(PaymentMethodType.CASH)"
            [disabled]="!isPaymentMethodAllowed(PaymentMethodType.CASH)"
            class="add-method-btn"
            aria-label="Add cash payment method"
          >
            <mat-icon aria-hidden="true">add</mat-icon>
            CASH
          </button>
          <!-- Over/short (QA 2026-10-04): keep an overpayment instead of giving change, or accept
               a shortfall, within the configured thresholds — the drawer then still balances. -->
          <button
            mat-stroked-button
            color="primary"
            (click)="addOverShort()"
            [disabled]="showOverShort || paymentMethods.length === 0"
            class="add-method-btn"
            aria-label="Add over/short adjustment"
          >
            <mat-icon aria-hidden="true">add</mat-icon>
            OVER/SHORT
          </button>
        </div>
      </div>

      <!-- Payment method cards -->
      @if (paymentMethods.length > 0) {
        <div class="payment-methods-list" role="list" aria-label="Payment methods">
          @for (method of paymentMethods; track method.id) {
            @if (method.type === PaymentMethodType.CHECK) {
              <app-payment-method-check
                [method]="method"
                (removed)="removePaymentMethod($event)"
                (amountChanged)="onAmountChanged($event)"
              ></app-payment-method-check>
            }
            @if (method.type === PaymentMethodType.CASH) {
              <app-payment-method-cash
                [method]="method"
                (removed)="removePaymentMethod($event)"
                (amountChanged)="onAmountChanged($event)"
              ></app-payment-method-cash>
            }
            @if (method.type === PaymentMethodType.DEBIT_CARD) {
              <app-payment-method-debit
                [method]="method"
                [accountId]="customerId"
                [returns]="basketItems"
                (removed)="removePaymentMethod($event)"
                (amountChanged)="onAmountChanged($event)"
                (methodUpdated)="onDebitMethodUpdated($event)"
              ></app-payment-method-debit>
            }
          }
        </div>
      }

      @if (showOverShort) {
        <div class="over-short-entry">
          <mat-form-field appearance="outline" class="over-short-field">
            <mat-label>Over / Short</mat-label>
            <span matTextPrefix>$&nbsp;</span>
            <input matInput type="number" step="0.01" [(ngModel)]="overShort" placeholder="0.00"
                   (focus)="$any($event.target).select()" aria-label="Over or short amount">
            <mat-hint>+ over kept (max {{ overThreshold | currency }}), − short accepted (max {{ underThreshold | currency }})</mat-hint>
          </mat-form-field>
          <button mat-icon-button (click)="removeOverShort()" aria-label="Remove over/short">
            <mat-icon>delete</mat-icon>
          </button>
          @if (getOverShortError(); as err) {
            <div class="over-short-error" role="alert">{{ err }}</div>
          }
        </div>
      }

      <!-- Empty payment method state -->
      @if (paymentMethods.length === 0) {
        <div class="empty-payment-methods" role="status">
          <mat-icon aria-hidden="true">payment</mat-icon>
          <p>No payment method added. Use the buttons above to add one.</p>
        </div>
      }

      <!-- ===== Totals section ===== -->
      <div
        class="totals-section"
        aria-live="polite"
        aria-atomic="true"
        aria-label="Payment totals"
      >
        <div class="totals-header" aria-hidden="true">Return totals / Total amount due</div>

        <div class="totals-rows">
          <!-- Credit (if any) -->
          @if (getHasCredit()) {
            <div class="total-row credit-row">
              <span class="total-row-label">Credit</span>
              <span class="total-row-value credit">-{{ getTotalCredit() | currency }}</span>
            </div>
          }

          <!-- Amount due -->
          <div class="total-row">
            <span class="total-row-label">Total amount due</span>
            <span class="total-row-value">{{ getTotalAmountDue() | currency }}</span>
          </div>

          <!-- Card fee (only when a non-zero fee is actually owed — Catalis-processed
               cards are always fee-free here, the county records principal only) -->
          @if (getTotalCardFees() > 0) {
            <div class="total-row card-fee-row">
              <span class="total-row-label">Card fee</span>
              <span class="total-row-value">{{ getTotalCardFees() | currency }}</span>
            </div>
          }

          <!-- Total payment -->
          <div class="total-row total-payment-row">
            <span class="total-row-label">Total Payment</span>
            <span class="total-row-value total-payment">{{ getTotalPayment() | currency }}</span>
          </div>

          @if (overShort) {
            <div class="total-row">
              <span class="total-row-label">{{ overShort > 0 ? 'Over (kept)' : 'Short (accepted)' }}</span>
              <span class="total-row-value">{{ overShort | currency }}</span>
            </div>
          }

          <!-- Remaining. Within the cash allowance it is a shortfall the collector absorbs
               and the return still settles, so it must not read like an outstanding balance. -->
          @if (getRemainingAmount() > 0) {
            <div class="total-row remaining-row">
              <span class="total-row-label remaining-label">
                {{ isShortfallAcceptable() ? 'Short' : 'Remaining amount' }}
              </span>
              <span class="total-row-value" [class.remaining]="!isShortfallAcceptable()"
                    [class.within-allowance]="isShortfallAcceptable()">
                {{ getRemainingAmount() | currency }}
              </span>
            </div>
          }

          <!-- Cash change due — money handed back to the customer (not an over/short) -->
          @if (getCashChangeDue() > 0) {
            <div class="total-row over-row" role="status">
              <span class="total-row-label over-label">
                <mat-icon class="over-short-icon" aria-hidden="true">payments</mat-icon>
                Cash Change Due
              </span>
              <span class="total-row-value over">{{ getCashChangeDue() | currency }}</span>
            </div>
          }

          @if (isExact() && getTotalPayment() > 0) {
            <div class="total-row exact-row">
              <span class="total-row-label">Cash change due</span>
              <span class="total-row-value exact">$0.00</span>
            </div>
          }

          <!-- Card/check cannot overpay — only cash can take change -->
          @if (hasNonCashOverpay()) {
            <div class="total-row remaining-row" role="alert">
              <span class="total-row-label remaining-label">
                <mat-icon class="over-short-icon" aria-hidden="true">error_outline</mat-icon>
                Card &amp; check cannot exceed amount due — only cash can take change
              </span>
            </div>
          }
        </div>
      </div>

      <!-- ===== Footer actions ===== -->
      <div class="footer-actions">
        <button mat-stroked-button color="primary" class="action-btn go-back-btn" (click)="goBack()">
          <mat-icon aria-hidden="true">arrow_back</mat-icon>
          GO BACK
        </button>

        <button mat-stroked-button color="warn" class="action-btn cancel-btn" (click)="cancel()">
          <mat-icon aria-hidden="true">close</mat-icon>
          CANCEL
        </button>

        <button
          mat-flat-button
          color="primary"
          class="action-btn process-btn"
          [disabled]="!canProcessPayment() || isProcessing"
          (click)="processPayment()"
          [attr.aria-busy]="isProcessing"
          [attr.aria-label]="isProcessing ? 'Processing payment, please wait' : (paymentType === PaymentType.MAIL_IN ? 'Register payment' : 'Process payment')"
        >
          @if (isProcessing) {
            <mat-spinner diameter="18" class="btn-spinner"></mat-spinner>
          } @else {
            <mat-icon aria-hidden="true">{{ getTotalAmountDue() <= 0 && paymentMethods.length === 0 ? 'receipt_long' : 'attach_money' }}</mat-icon>
          }
          {{ getTotalAmountDue() <= 0 && paymentMethods.length === 0 ? 'FILE RETURN' : (paymentType === PaymentType.MAIL_IN ? 'REGISTER PAYMENT' : 'PROCESS PAYMENT') }}
        </button>
      </div>

    </section>
  </div>
</div>

}
